import { useState } from "react";
import { useSelector, useDispatch } from "react-redux";

import { Typography } from "@mui/material";

import { ActionButton } from "components/ActionButton";
import { Tooltip } from "components/Tooltip";

import { i18n } from "services/i18nService";
import { isValid as isValidCart } from "services/sosInventoryService/cart/isValid";
import {
  CONNECTOR_APP_ID,
  PRIVATE_APP_ID,
  CUSTOM_APP_ID,
  APP_CREDS_ID,
} from "services/sosInventoryService/cart/schema";
import {
  postRecord,
  putRecord,
  getShopifyConfig,
} from "services/sosInventoryService/sosApi";
import { setPageDirty } from "services/utility/edit";
import { handleProgramError } from "services/utility/errors";
import { sanitizeShopDomain } from "services/utility/formatting";
import { isPersistedRecord } from "services/utility/misc";

import { openAlert } from "globalState/alertSlice";
import {
  editModalLoadingIndicatorOn,
  editModalLoadingIndicatorOff,
} from "globalState/loadingSlice";

import {
  SHOPIFY_AUTH_REDIRECT_URL,
  SOS_TEST_ACCOUNT_ID,
  SHOPIFY_TEST_ACCOUNT_ID,
} from "appConstants";

export function ShopifyConnectButton(props) {
  const { record, objectType, onSave, setErrors } = props;
  const dispatch = useDispatch();
  const [isConnecting, setIsConnecting] = useState(false);

  const companyId = useSelector(
    (state) => state.userCompanySettings.settings.id
  );

  const isWhiteListedAccount =
    companyId === SHOPIFY_TEST_ACCOUNT_ID || companyId === SOS_TEST_ACCOUNT_ID;

  const isCustomOrPrivate =
    record.shopifyAppType === PRIVATE_APP_ID ||
    record.shopifyAppType === CUSTOM_APP_ID ||
    record.shopifyAppType === APP_CREDS_ID;

  async function handleConnectToShopify() {
    setIsConnecting(true);

    try {
      const shopDomain = sanitizeShopDomain(record.storeIdentifier);
      const updatedRecord = {
        ...record,
        storeIdentifier: shopDomain,
        authorizationField1: null,
        shopifyAppType: CONNECTOR_APP_ID,
      };

      // 2. Validate the cart before saving
      const validationErrors = isValidCart(updatedRecord);
      if (validationErrors.messages.length > 0) {
        setErrors(validationErrors);
        setIsConnecting(false);
        return;
      }

      // 3. Save the cart and get the cart ID
      dispatch(editModalLoadingIndicatorOn());
      const persistenceFunc = isPersistedRecord(record.id)
        ? putRecord
        : postRecord;
      const {
        success,
        record: savedCart,
        message,
      } = await persistenceFunc(objectType, updatedRecord);
      dispatch(editModalLoadingIndicatorOff());
      if (!success) {
        const errorMessage = message || i18n("shopify.SaveCartError");
        dispatch(openAlert({ type: "error", message: errorMessage }));
        setIsConnecting(false);
        return;
      }

      if (!savedCart.id) {
        const errorMessage = i18n("shopify.CartIdError");
        dispatch(openAlert({ type: "error", message: errorMessage }));
        setIsConnecting(false);
        return;
      }

      // Notify parent component of the save
      if (onSave) {
        onSave(savedCart);
      }

      setPageDirty(false);

      // 4. Fetch Shopify OAuth config from backend
      const configResponse = await getShopifyConfig();
      if (!configResponse.success) {
        const errorMessage =
          configResponse.message || i18n("shopify.ConfigFetchError");
        dispatch(openAlert({ type: "error", message: errorMessage }));
        setIsConnecting(false);
        return;
      }

      const { clientId, scopes } = configResponse.data;

      // 5. Generate state (nonce) for security and append cart ID
      const nonce = crypto.randomUUID();
      const state = `${nonce}_${savedCart.id}`;

      // 6. Save state to sessionStorage
      sessionStorage.setItem("shopify_oauth_state", state);

      // 7. Construct Shopify Authorization URL and redirect
      const shopifyAuthUrl =
        `https://${shopDomain}.myshopify.com/admin/oauth/authorize?` +
        `client_id=${encodeURIComponent(clientId)}&` +
        `scope=${encodeURIComponent(scopes)}&` +
        `redirect_uri=${encodeURIComponent(SHOPIFY_AUTH_REDIRECT_URL)}&` +
        `state=${encodeURIComponent(state)}`;

      // Redirect to Shopify
      window.location.href = shopifyAuthUrl;
    } catch (error) {
      handleProgramError(error);
      dispatch(openAlert({ type: "error", message: i18n("shopify.UnexpectedError") }));
      setIsConnecting(false);
    }
  }

  if (isCustomOrPrivate || record.authorizationField1) {
    return null;
  }

  return (
    <Tooltip
      title={
        record.storeIdentifier
          ? ""
          : "Please enter a Shop value before connecting to Shopify"
      }
    >
      <span style={{ width: "100%" }}>
        <ActionButton
          disabled={
            !record.storeIdentifier || isConnecting || !isWhiteListedAccount
          }
          onClick={handleConnectToShopify}
          sx={{ width: "100%" }}
        >
          {isConnecting
            ? i18n("shopify.Connecting")
            : isCustomOrPrivate
            ? i18n("shopify.UpdateConnectionButton")
            : i18n("shopify.ConnectButton")}
        </ActionButton>
        {!isWhiteListedAccount && (
          <Typography
            variant="body2"
            sx={{
              mt: 1,
              color: "warning.error",
              fontSize: "0.8rem",
              fontStyle: "italic",
              fontWeight: 500,
            }}
          >
            The Shopify connection flow is currently being updated. This button
            will be enabled once the update is complete.
          </Typography>
        )}
      </span>
    </Tooltip>
  );
}
